/**
 * The non-privileged read view of an Peer Interface (ADR-036 §D-7;
 * REQ-PEER-042).
 *
 * What an `operator` or `analyst` sees: **name, type, state, connection health,
 * and a filter summary** — and nothing else, because Consumer endpoints,
 * cert-material references and filter *literals* describe collection posture
 * (which sensors, which areas, which object classes a downstream Consumer
 * receives) and stay `admin` / `support_engineer` only.
 *
 * **The BFF already withheld those fields.** This component does not re-apply
 * the rule; it renders what arrived. A privileged session viewing without
 * editing sees the same summary with the editor a click away — the split is in
 * the response, not in this render path.
 *
 * # Why any of it is visible at all
 *
 * Egress interfaces are the flows *leaving* the system. An operator who cannot
 * see them cannot notice one running that should not be, and suppression that is
 * invisible is worse than suppression (``security.md``, Flow L §11). So the
 * state and the connection roster are readable by every authenticated role, and
 * a "not configured" filter is stated as *emits nothing* rather than left to
 * read as *unfiltered* — the fail-safe reading is the surprising one.
 *
 * Cross-references:
 *   - ADR-036 §D-7 (read field-set); REQ-PEER-042.
 *   - Flow L §1 (mutation controls absent, not disabled); Flow W §2.
 */
import { findInterfaceType, findTransportSecurity } from "@/peers/peer-catalog";
import { resolveEffectiveState, type ChainLink } from "@/peers/peer-effective-state";
import { PeerSuppressionNote } from "@/peers/peer-suppression-note";
import { PeerSection } from "@/peers/peer-section";
import { StatePill } from "@/peers/peer-state-controls";
import type { PeerInterfaceView } from "@/peers/peer-model";

/** Props for {@link PeerInterfaceSummary}. */
export interface PeerInterfaceSummaryProps {
  view: PeerInterfaceView;
  /**
   * The owning chain, root-first — ``[peer]`` when mounted in the dialog.
   *
   * A non-privileged reader gets the ancestor annotation too: every state it is
   * derived from is in the all-roles field-set, and an operator who cannot see
   * that a peer is holding an interface down is exactly the reader who most
   * needs to (REQ-UI-144; ``security.md``).
   */
  ancestors?: readonly ChainLink[];
}

/** Render the field-limited read view of one interface. */
export function PeerInterfaceSummary({
  view,
  ancestors = [],
}: PeerInterfaceSummaryProps): React.ReactElement {
  const descriptor = findInterfaceType(view.type_id);
  const chain: readonly ChainLink[] = [...ancestors, { name: view.name, state: view.state }];
  // No scroller of its own — the dialog is one scrollable pane (REQ-UI-141).
  return (
    <div data-testid="peer-interface-summary">
      <PeerSection id="identity" title="Interface">
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-body-sm">
          <dt className="text-text-secondary">Name</dt>
          <dd className="text-text-primary">{view.name}</dd>
          <dt className="text-text-secondary">Type</dt>
          <dd className="text-text-primary">{descriptor?.label ?? view.type_id}</dd>
          {/* Both are in the ALL-ROLES field-set and are read here for the
              reason this whole view exists (REQ-UI-152 / -153): they are the
              two facts a non-privileged reader most needs — whether mission
              data leaves in the clear, and which renderer produced what left.
              Both are postures, so neither discloses an endpoint, a credential
              path or a filter literal, which is what this view withholds. The
              conversion PROFILE is deliberately absent: it names a specific
              installed artifact by digest, which is deployment shape. */}
          <dt className="text-text-secondary">Transport security</dt>
          <dd className="text-text-primary" data-testid="peer-summary-transport-security">
            {findTransportSecurity(view.transport_security)?.label ?? view.transport_security}
          </dd>
          <dt className="text-text-secondary">Conversion</dt>
          <dd className="text-text-primary" data-testid="peer-summary-conversion-mode">
            {view.conversion_mode === "engine"
              ? "Engine — the installed conversion profile is authoritative"
              : "Legacy — the built-in renderer is authoritative"}
          </dd>
          <dt className="text-text-secondary">Loaded</dt>
          <dd className="text-text-primary">{view.enabled ? "Enabled" : "Disabled"}</dd>
          <dt className="text-text-secondary">State</dt>
          <dd className="flex flex-wrap items-center gap-2">
            <StatePill state={view.state} />
            <PeerSuppressionNote
              resolved={resolveEffectiveState(ancestors, view.state)}
              testId="summary-interface"
            />
          </dd>
        </dl>
        {/* The consequence is stated to a non-privileged reader too. They cannot
            change it, which is exactly why they need to be able to SEE it — an
            unencrypted egress path nobody can notice is the sharpest case of the
            thing this view exists to prevent (REQ-UI-152; `security.md`). */}
        {view.transport_security === "plaintext" && (
          <p
            role="status"
            data-testid="peer-summary-plaintext-consequence"
            className="mt-2 rounded border border-destructive px-2 py-1 text-meta text-destructive"
          >
            {findTransportSecurity("plaintext")?.consequence}
          </p>
        )}
      </PeerSection>

      <PeerSection id="connections" title="Consumer connections">
        {view.connections.length === 0 ? (
          <p className="text-body-sm text-text-muted">
            No connections. This interface has nowhere to send to and emits nothing.
          </p>
        ) : (
          <ul className="flex flex-col gap-1" data-testid="peer-summary-connections">
            {view.connections.map((connection) => (
              <li key={connection.connection_id} className="flex items-center gap-2 text-body-sm">
                <span className="min-w-0 flex-1 truncate text-text-primary">{connection.name}</span>
                <span className="text-meta capitalize text-text-secondary">
                  {connection.role.replace("_", " ")}
                </span>
                <StatePill state={connection.state} />
                <PeerSuppressionNote
                  resolved={resolveEffectiveState(chain, connection.state)}
                  testId={`summary-connection-${connection.name}`}
                />
              </li>
            ))}
          </ul>
        )}
      </PeerSection>

      <PeerSection id="filter" title="Filter">
        <FilterSummaryBody view={view} />
      </PeerSection>
    </div>
  );
}

/** The non-disclosing filter description — axes, not values. */
function FilterSummaryBody({ view }: { view: PeerInterfaceView }): React.ReactElement {
  const summary = view.filter_summary;
  if (!summary.configured) {
    return (
      <p className="text-body-sm text-warning" data-testid="peer-summary-filter-unconfigured">
        No filter is configured, so this interface emits nothing. Filters are default-deny —
        unconfigured is not unfiltered.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1" data-testid="peer-summary-filter">
      <p className="text-body-sm text-text-primary">
        {summary.clause_count} {summary.clause_count === 1 ? "condition" : "conditions"} across{" "}
        {summary.selector_kinds.length}{" "}
        {summary.selector_kinds.length === 1 ? "selector" : "selectors"}.
      </p>
      <ul className="flex flex-wrap gap-1">
        {summary.selector_kinds.map((kind) => (
          <li
            key={kind}
            className="rounded border border-border bg-bg-elevated px-1.5 py-0.5 font-mono text-meta text-text-secondary"
          >
            {kind}
          </li>
        ))}
      </ul>
      <p className="text-meta text-text-muted">
        Which values each condition allows is restricted to administrators and support engineers.
      </p>
    </div>
  );
}
